import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { DateTime } from "luxon";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

export enum MonthPeriodLabel {
    Month = "month",
    Period = "period"
}

export class CustomDate {
    public readonly date: DateTime;
    public readonly weekNumber?: number;
    public readonly periodNumber: number;
    public readonly quarterNumber: number;
    public readonly year: number;

    constructor(
        date: DateTime,
        year: number,
        quarterNumber: number,
        periodNumber: number,
        weekNumber?: number,
    ) {
        this.date = date;
        this.weekNumber = weekNumber;
        this.periodNumber = periodNumber;
        this.quarterNumber = quarterNumber;
        this.year = year;
    }

    get financialYearNumber(): number {
        return Number(String(this.year).slice(2));
    }

    get period(): string {
        return `P${this.periodNumber} ${this.year}`;
    }

    get quarter(): string {
        return `Q${this.quarterNumber} ${this.year}`;
    }

    get quarterLabel(): string {
        return `Q${this.quarterNumber} FY${this.financialYearNumber}`;
    }

    get periodLabel(): string {
        return `P${this.periodNumber} FY${this.financialYearNumber}`;
    }

    get weekLabel(): string {
        return `W${this.weekNumber} P${this.periodNumber} FY${this.financialYearNumber}`;
    }

    get financialYearLabel(): string {
        return `FY${this.financialYearNumber}`;
    }
}

// ToDo: Replace below function with a selector which calculates the number of periods 
// export const loadNumberOfPeriods = (useCustomCalendar: boolean, referenceDate: DateTime): AppThunk<Promise<number>> => async (dispatch) => {
//     try {
//         if (!useCustomCalendar) {
//             return 12;
//         }

//         const financialYearQuery = {
//             filters: [{
//                 "member": "D_Date.Date",
//                 "operator": "equals",
//                 "values": [referenceDate]
//             }],
//             dimensions: [
//                 "D_Date.FY"
//             ]
//         };
//         const financialYearResultSet = await dispatch(cubeLoad(financialYearQuery)) as unknown as ResultSet;
//         const financialYearNumber = financialYearResultSet.rawData()[0]["D_Date.FY"];

//         const countQuery = {
//             filters: [{
//                 "member": "D_Date.FY",
//                 "operator": "equals",
//                 "values": [String(financialYearNumber)]
//             }],
//             measures: [
//                 "D_Date.CountDistinctPeriods",
//             ]
//         };
//         const countResultSet = await dispatch(cubeLoad(countQuery)) as unknown as ResultSet;
//         return countResultSet.rawData()[0]["D_Date.CountDistinctPeriods"];
//     } catch (error) {
//         dispatch(logError("Error loading number of periods.", error));
//         throw error;
//     }
// };

export const getDateDimensionNames = (useCustomCalendar: boolean) => ({
    weekStartDate: "WeekStartDate",
    monthPeriodNumber: useCustomCalendar ? "PeriodNo" : "MonthNo",
    monthPeriodStartDate: useCustomCalendar ? "PeriodStartDate" : "MonthStartDate",
    monthPeriodEndDate: useCustomCalendar ? "PeriodEndDate" : "MonthEndDate",
    maxMonthPeriodStartDate: useCustomCalendar ? "MaxPeriodStartDate" : "MaxMonthStartDate",
    maxMonthPeriodEndDate: useCustomCalendar ? "MaxPeriodEndDate" : "MaxMonthEndDate",
    quarterNumber: "QuarterNo",
    yearNumber: useCustomCalendar ? "FY" : "Year"
});

export const loadCustomCalendar = (useCustomCalendar: boolean, referenceDate: DateTime): AppThunk<Promise<CustomDate[]>> => async (dispatch) => {
    try {
        if (!useCustomCalendar) {
            return [];
        }

        const forecastStartDate = referenceDate.weekday === 7 ? referenceDate.plus({ days: 1 }) : referenceDate.customStartOfWeek();
        const nextYearEnd = forecastStartDate.plus({ weeks: 52 }).minus({ days: 1 }).endOf('day');
        const threeYearsPrior = forecastStartDate.minus({ weeks: 52 * 3 }).startOf('day');

        const dateDimensions = getDateDimensionNames(useCustomCalendar);
        const query = {
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [threeYearsPrior, nextYearEnd]
            }],
            dimensions: [
                `D_Date.${dateDimensions.yearNumber}`,
                `D_Date.${dateDimensions.quarterNumber}`,
                `D_Date.${dateDimensions.monthPeriodNumber}`,
                `D_Date.${dateDimensions.weekStartDate}`,
            ],
            order: [
                [`D_Date.${dateDimensions.weekStartDate}`, "asc"]
            ]
        };
        const resultSet = await dispatch(cubeLoad(query)) as unknown as ResultSet;
        let weekNumber = 1;
        let previousPeriodNumber = 0;
        return resultSet.rawData().map(row => {
            weekNumber++;
            if (previousPeriodNumber !== Number(row["D_Date.PeriodNo"])) {
                weekNumber = 1;
            }
            previousPeriodNumber = row["D_Date.PeriodNo"];
            return new CustomDate(
                DateTime.fromISO(row[`D_Date.${dateDimensions.weekStartDate}`], { zone: "utc" }),
                Number(row[`D_Date.${dateDimensions.yearNumber}`]),
                Number(row[`D_Date.${dateDimensions.quarterNumber}`]),
                Number(row[`D_Date.${dateDimensions.monthPeriodNumber}`]),
                Number(weekNumber)
            );
        });
    } catch (error) {
        dispatch(logError("Error loading custom calendar.", error));
        throw error;
    }
};
